import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, ShoppingBag } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { GoodEyesLogo } from "./GoodEyesLogo";
import { TID } from "@/lib/testIds";
import { useEnquiry } from "@/context/EnquiryContext";

const links = [
  { label: "The Range", href: "/shop",    tid: TID.nav.linkRange },
  { label: "About",     href: "/about",   tid: TID.nav.linkStory },
  { label: "Contact",   href: "/contact", tid: TID.nav.linkStockists },
];

export const Nav = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { totalUnits, setOpen: setEnquiryOpen } = useEnquiry();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid={TID.nav.root}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white ${
        scrolled
          ? "border-b border-[color:var(--ge-navy)]/15 shadow-[0_4px_20px_-16px_rgba(0,31,91,0.2)]"
          : "border-b border-transparent"
      }`}
    >
      <div className="ge-container flex justify-between items-center py-3 md:py-4 md:grid md:grid-cols-3">
        {/* Logo (left) */}
        <div className="flex justify-start">
          <Link to="/" data-testid={TID.nav.logo} className="inline-flex items-center py-1" aria-label="Good Eyes home">
            <GoodEyesLogo height={80} />
          </Link>
        </div>

        {/* Center nav */}
        <nav className="hidden md:flex items-center justify-center gap-10">
          {links.map((l) => (
            <Link
              key={l.href}
              to={l.href}
              data-testid={l.tid}
              className={`text-[11px] uppercase tracking-[0.24em] font-semibold text-[color:var(--ge-navy)] hover:text-[color:var(--ge-red)] transition-colors ${
                location.pathname === l.href ? "text-[color:var(--ge-red)]" : ""
              }`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        {/* Right: Enquiry bag + Shop Now */}
        <div className="flex justify-end items-center gap-2 md:gap-3">
          <button
            type="button"
            onClick={() => setEnquiryOpen(true)}
            data-testid="nav-enquiry-bag"
            aria-label={`Open enquiry (${totalUnits} item${totalUnits === 1 ? "" : "s"})`}
            className="relative inline-flex items-center justify-center h-11 w-11 rounded-full border border-[color:var(--ge-navy)]/20 text-[color:var(--ge-navy)] hover:bg-[color:var(--ge-cream)] transition-colors"
          >
            <ShoppingBag size={18} />
            {totalUnits > 0 && (
              <span
                data-testid="nav-enquiry-count"
                className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full text-[10px] font-bold flex items-center justify-center"
                style={{ background: "var(--ge-red)", color: "#fff" }}
              >
                {totalUnits}
              </span>
            )}
          </button>
          <Link
            to="/shop"
            data-testid={TID.nav.cta}
            className="hidden md:inline-flex items-center gap-1.5 rounded-full bg-[color:var(--ge-navy)] text-white px-6 py-3 text-xs uppercase tracking-[0.22em] font-semibold hover:bg-[color:var(--ge-red)] transition-colors"
          >
            Shop Now
            <ArrowUpRight size={14} />
          </Link>
          <button
            className="md:hidden text-[color:var(--ge-navy)]"
            onClick={() => setOpen((v) => !v)}
            data-testid={TID.nav.mobileToggle}
            aria-label="Toggle menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden bg-white border-t border-[color:var(--ge-navy)]/10 overflow-hidden"
          >
            <div className="flex flex-col px-6 py-6 gap-4">
              {links.map((l) => (
                <Link
                  key={l.href}
                  to={l.href}
                  data-testid={l.tid + "-mobile"}
                  className="text-sm uppercase tracking-[0.22em] font-semibold text-[color:var(--ge-navy)]"
                >
                  {l.label}
                </Link>
              ))}
              <Link
                to="/shop"
                className="mt-2 inline-flex items-center justify-center rounded-full bg-[color:var(--ge-navy)] text-white px-5 py-3 text-xs uppercase tracking-[0.22em]"
                data-testid={TID.nav.cta + "-mobile"}
              >
                Shop Now
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Nav;
